/** Contents of the shell-owned Display pocket; no second trigger or portal. */
import { useState } from "react";
import {
  Button,
  ChipGroup,
  PocketRow,
  PocketSection,
  ToggleSwitch,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useTasksStore } from "@/stores/tasks-store";
import type { TaskViewMode } from "@/stores/task-view-session";
import type { TaskSource } from "@/api/tasks";
import { useT, type Translator } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import type en from "@/i18n/messages/frames-taskboard/en";
import {
  TASK_PRIORITY_LABEL_KEYS,
  TASK_STATUS_LABEL_KEYS,
} from "./task-metadata-chips";

type DisplayStore = ReturnType<typeof useTasksStore.getState>;
/** Catalogue keys of plain labels (no params). */
type LabelKey = {
  [K in keyof typeof en]: (typeof en)[K] extends `${string}{${string}`
    ? never
    : (typeof en)[K] extends string
      ? K
      : never;
}[keyof typeof en];

const LAYOUT_LABEL_KEYS = {
  board: "layoutBoard",
  list: "layoutList",
  calendar: "layoutCalendar",
} as const satisfies Record<TaskViewMode, LabelKey>;
const SORT_LABEL_KEYS = {
  priority: "sortPriority",
  due_date: "sortDueDate",
  created: "sortCreated",
  status: "sortStatus",
  agent: "sortAgent",
} as const satisfies Record<DisplayStore["sortBy"], LabelKey>;
const STATUS_FILTER_LABEL_KEYS = {
  all: "all",
  ...TASK_STATUS_LABEL_KEYS,
} as const satisfies Record<DisplayStore["filterStatus"], LabelKey>;
const PRIORITY_FILTER_LABEL_KEYS = {
  all: "all",
  ...TASK_PRIORITY_LABEL_KEYS,
} as const satisfies Record<DisplayStore["filterPriority"], LabelKey>;
const SOURCE_FILTER_LABEL_KEYS = {
  all: "all",
  note: "sourceNote",
  chat: "sourceChat",
  heartbeat: "sourceHeartbeat",
  schedule: "sourceSchedule",
  comment_mention: "sourceCommentMention",
  task_assigned: "sourceTaskAssigned",
  delegation: "sourceDelegation",
  event: "sourceEvent",
  template: "sourceTemplate",
} as const satisfies Record<TaskSource | "all", LabelKey>;
const CREATED_FILTER_LABEL_KEYS = {
  all: "all",
  today: "today",
  this_week: "thisWeek",
  this_month: "thisMonth",
} as const satisfies Record<DisplayStore["filterCreatedRange"], LabelKey>;

/** Options in the key map's order, labelled in the app language. */
function options(
  t: Translator<typeof en>,
  labelKeys: Readonly<Record<string, LabelKey>>,
) {
  return Object.entries(labelKeys).map(([value, key]) => ({
    value,
    label: t(key),
  }));
}

function ValueRow({
  id,
  name,
  value,
  options,
  onChange,
}: {
  /** Stable id for test ids; `name` is the translated row label. */
  id: string;
  name: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  const t = useT(framesTaskboardMessages);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto w-full justify-between gap-appkit-xs rounded-preview px-appkit-xs py-appkit-sm text-appkit-body"
          data-testid={`display-filter-${id}`}
        >
          <span className="truncate">{name}</span>
          <span className="min-w-0 truncate text-muted">
            {options.find((option) => option.value === value)?.label ??
              t("all")}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            data-testid={`display-${id}-${option.value || "all"}`}
            onSelect={() => onChange(option.value)}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
/** Display for one board: its own layout and the shared filters. */
export function DisplayPopover({
  boardId,
  agentOptions = [],
}: {
  boardId: string;
  agentOptions?: { id: string; name: string }[];
}) {
  const t = useT(framesTaskboardMessages);
  const [page, setPage] = useState<"main" | "filters">("main");
  const state = useTasksStore();
  const view = state.boards[boardId]?.view ?? "board";
  const active = [
    state.filterPriority !== "all",
    !!state.filterAgent,
    state.filterSource !== "all",
    state.filterCreatedRange !== "all",
    view !== "board" && state.filterStatus !== "all",
  ].filter(Boolean).length;
  return (
    <div
      className="w-[296px] max-w-full space-y-appkit-xs"
      data-testid="display-popover-panel"
    >
      {page === "main" ? (
        <>
          <PocketSection title={t("layout")}>
            <ChipGroup
              value={view}
              onChange={(value) => state.setBoardView(boardId, value)}
              variant="segmented"
              ariaLabel={t("boardLayout")}
              options={(["board", "list", "calendar"] as const).map(
                (value) => ({
                  value,
                  label: t(LAYOUT_LABEL_KEYS[value]),
                  testId: `display-layout-${value}`,
                }),
              )}
            />
          </PocketSection>
          {view !== "board" && (
            <PocketRow
              testId="display-completed-row"
              title={t("hideCompleted")}
              trailing={
                <ToggleSwitch
                  checked={!state.showCompleted}
                  onCheckedChange={(checked) =>
                    state.setShowCompleted(!checked)
                  }
                  aria-label={t("hideCompleted")}
                  data-testid="display-completed-toggle"
                />
              }
            />
          )}
          {view === "list" && (
            <div className="flex items-center">
              <div className="min-w-0 flex-1">
                <ValueRow
                  id="sort"
                  name={t("sort")}
                  value={state.sortBy}
                  options={options(t, SORT_LABEL_KEYS)}
                  onChange={(value) =>
                    state.setSortBy(value as typeof state.sortBy)
                  }
                />
              </div>
              <Button
                size="iconSm"
                variant="ghost"
                aria-label={t(
                  state.sortDirection === "asc"
                    ? "sortDescending"
                    : "sortAscending",
                )}
                data-testid="display-sort-direction"
                onClick={() =>
                  state.setSortDirection(
                    state.sortDirection === "asc" ? "desc" : "asc",
                  )
                }
              >
                <Icons.ArrowDown
                  className={
                    state.sortDirection === "asc"
                      ? "h-4 w-4 rotate-180"
                      : "h-4 w-4"
                  }
                />
              </Button>
            </div>
          )}
          <PocketRow
            testId="display-filter"
            title={t("filter")}
            trailing={
              active ? t("filtersActive", { count: active }) : t("allTasks")
            }
            chevron
            onSelect={() => setPage("filters")}
          />
        </>
      ) : (
        <>
          <Button
            size="sm"
            variant="ghost"
            data-testid="display-filter-back"
            onClick={() => setPage("main")}
          >
            <Icons.ChevronLeft className="h-4 w-4" />
            {t("filter")}
          </Button>
          {view !== "board" && (
            <ValueRow
              id="status"
              name={t("filterStatus")}
              value={state.filterStatus}
              options={options(t, STATUS_FILTER_LABEL_KEYS)}
              onChange={(value) =>
                state.setFilterStatus(value as typeof state.filterStatus)
              }
            />
          )}
          <ValueRow
            id="priority"
            name={t("filterPriority")}
            value={state.filterPriority}
            options={options(t, PRIORITY_FILTER_LABEL_KEYS)}
            onChange={(value) =>
              state.setFilterPriority(value as typeof state.filterPriority)
            }
          />
          <ValueRow
            id="agent"
            name={t("filterAgent")}
            value={state.filterAgent ?? ""}
            options={[
              { value: "", label: t("all") },
              ...agentOptions.map((agent) => ({
                value: agent.id,
                label: agent.name,
              })),
            ]}
            onChange={(value) => state.setFilterAgent(value || null)}
          />
          <ValueRow
            id="source"
            name={t("filterSource")}
            value={state.filterSource}
            options={options(t, SOURCE_FILTER_LABEL_KEYS)}
            onChange={(value) =>
              state.setFilterSource(value as typeof state.filterSource)
            }
          />
          <ValueRow
            id="created"
            name={t("filterCreated")}
            value={state.filterCreatedRange}
            options={options(t, CREATED_FILTER_LABEL_KEYS)}
            onChange={(value) =>
              state.setFilterCreatedRange(
                value as typeof state.filterCreatedRange,
              )
            }
          />
          {active > 0 && (
            <Button
              size="sm"
              variant="ghost"
              data-testid="display-reset-all"
              onClick={() => state.resetDisplay(boardId)}
            >
              {t("resetAll")}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
